import React, { useEffect, useState, useCallback } from "react";
import {
  Col,
  Row,
  Button,
  Dropdown,
  DropdownButton,
  Form,
} from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { FaTrash, FaCamera, FaUpload } from "react-icons/fa";
import Webcam from "react-webcam";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import baseURL from "../../../../../../Url/NodeBaseURL";
import InputField from "../../EstimatesForm/InputFieldEstimates";

import {
  PRODUCT_FORM_SCHEMA,
  DEFAULT_PRODUCT_FORM_DATA,
  shouldShowField,
  validateProductFormData
} from "../data/productFormSchema";
import "./ProductForm.css";

const ProductForm = ({
  formData,
  // Image handling
  handleImageChange,
  fileInputRef,
  clearImage,
  captureImage,
  setShowWebcam,
  showWebcam,
  webcamRef,
  setShowOptions,
  showOptions,

  // Data sources
  products,
  data,
  categoryOptions,
  subcategoryOptions,
  designOptions,
  purityOptions,
  metaltypeOptions,

  // State flags
  isQtyEditable,
  isManualTotalPriceChange,
  setIsManualTotalPriceChange,

  // Additional data
  offers,
  initialProductFormData = {},
  rates = {},

  // Product form management props (from parent hook)
  productFormData,
  setProductFormData,
  handleChange,
  handleAdd,
  handleUpdate,
  isEditing,
  calculations,
  clearForm,
  updateFormDataWithCalculations,
  handleTotalPriceChange,

  // Additional handlers for field population
  handleBarcodeChange,
  handleProductNameChange,
  handleMetalTypeChange,
  handleDesignNameChange,
}) => {
  const navigate = useNavigate();
  //check date
  function isCurrentDate(dateString) {
    const today = new Date();
    const date = new Date(dateString);

    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }
  const isToday = isCurrentDate(formData.date);
  
  // Check if this is a tagged barcode product
  const isTagEntryProduct =
    !!productFormData.opentag_id &&
    String(productFormData.opentag_id).trim() !== "" &&
    String(productFormData.opentag_id).trim().toLowerCase() !== "null";

  // Validation state
  const [errors, setErrors] = useState({});

  // Derived state
  const isByFixed = productFormData.pricing === "By fixed";
  const isRunningTag = productFormData.pricing === "By Running Tag";

  // State to store full subcategory data for "By Running Tag" products
  const [selectedSubcategoryData, setSelectedSubcategoryData] = useState(null);

  // Fetch full subcategory details when product_name changes and pricing is "By Running Tag"
  useEffect(() => {
    const fetchSubcategoryDetails = async () => {
      if (!productFormData.product_name || productFormData.pricing !== "By Running Tag") {
        setSelectedSubcategoryData(null);
        return;
      }

      try {
        // Fetch all subcategories to find the one matching product_name
        const response = await axios.get(`${baseURL}/subcategory`);
        const allSubcategories = response.data?.data || [];
        
        const selectedSubCategory = allSubcategories.find(
          (cat) => cat.sub_category_name === productFormData.product_name
        );

        if (selectedSubCategory && selectedSubCategory.subcategory_id) {
          const detailResponse = await axios.get(
            `${baseURL}/subcategory/${selectedSubCategory.subcategory_id}`
          );
          setSelectedSubcategoryData(detailResponse.data);
        }
      } catch (error) {
        console.error("Error fetching subcategory details:", error);
        setSelectedSubcategoryData(null);
      }
    };

    fetchSubcategoryDetails();
  }, [productFormData.product_name, productFormData.pricing]);

  // Calculate selling purity for "By Running Tag" products
  const calculateSellingPurityForRunningTag = useCallback(() => {
    if (productFormData.pricing !== "By Running Tag") {
      return null;
    }

    const parsePercent = (val) => {
      if (val === null || val === undefined || val === "") return 0;
      const s = val.toString().replace("%", "").trim();
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    };

    // Get current tag data if it's a tag entry product
    const currentTag = isTagEntryProduct && productFormData.code
      ? data.find((tag) => String(tag.PCode_BarCode) === String(productFormData.code))
      : null;

    // Get purchase purity - prioritize productFormData (set from barcode scan), then tag data, then subcategory
    let purchasePuritySource = productFormData.pur_purityPercentage || productFormData.purchase_manual_purity;
    
    // If productFormData has pur_Purity, check if it's "Manual"
    if (!purchasePuritySource && productFormData.pur_Purity) {
      if (String(productFormData.pur_Purity).toLowerCase().includes("manual")) {
        purchasePuritySource = productFormData.pur_purityPercentage;
      } else {
        purchasePuritySource = productFormData.pur_Purity;
      }
    }
    
    // Fallback to tag or subcategory
    if (!purchasePuritySource) {
      purchasePuritySource = currentTag?.pur_purityPercentage || 
        currentTag?.pur_Purity ||
        selectedSubcategoryData?.purchase_manual_purity ||
        "";
    }
    
    const purchasePurity = parsePercent(purchasePuritySource);

    // Get purchase MC% (only if MC On is "MC %")
    let purchaseMC = 0;
    const mcOnValue = productFormData.pur_Making_Charges_On || productFormData.mc_on || currentTag?.pur_Making_Charges_On || "";
    
    if (mcOnValue === "MC %" || productFormData.pur_MC_Per_Gram || currentTag?.pur_MC_Per_Gram) {
      const mcSource = productFormData.pur_MC_Per_Gram ||
        productFormData.purchase_mc_percent ||
        currentTag?.pur_MC_Per_Gram ||
        selectedSubcategoryData?.purchase_mc_percent ||
        "";
      purchaseMC = parsePercent(mcSource);
    }

    // Get purchase wastage%
    const purchaseWastageSource = productFormData.pur_Wastage_Percentage ||
      productFormData.purchase_wastage_percent ||
      currentTag?.pur_Wastage_Percentage ||
      selectedSubcategoryData?.purchase_wastage_percent ||
      "";
    const purchaseWastage = parsePercent(purchaseWastageSource);

    // Get selling addon purity - check productFormData, then subcategory
    const sellingAddonPuritySource = productFormData.selling_addon_purity ||
      selectedSubcategoryData?.selling_addon_purity ||
      "";
    const sellingAddonPurity = parsePercent(sellingAddonPuritySource);

    // Get selling addon MC%
    const sellingAddonMCSource = productFormData.selling_addon_mc_percent ||
      selectedSubcategoryData?.selling_addon_mc_percent ||
      "";
    const sellingAddonMC = parsePercent(sellingAddonMCSource);

    // Get selling addon wastage%
    const sellingAddonWastageSource = productFormData.selling_addon_wastage_percent ||
      selectedSubcategoryData?.selling_addon_wastage_percent ||
      "";
    const sellingAddonWastage = parsePercent(sellingAddonWastageSource);

    // Selling Purity = Purchase Purity + Selling Addon Purity only
    // MC and Wastage are shown separately in their own fields (MC % and Wastage %)
    const totalPurity = purchasePurity + sellingAddonPurity;
    
    return totalPurity > 0 ? totalPurity.toFixed(2) : null;
  }, [
    productFormData.pricing,
    productFormData.code,
    productFormData.pur_purityPercentage,
    productFormData.purchase_manual_purity,
    productFormData.pur_Purity,
    productFormData.pur_Making_Charges_On,
    productFormData.mc_on,
    productFormData.pur_MC_Per_Gram,
    productFormData.purchase_mc_percent,
    productFormData.pur_Wastage_Percentage,
    productFormData.purchase_wastage_percent,
    productFormData.selling_addon_purity,
    productFormData.selling_addon_mc_percent,
    productFormData.selling_addon_wastage_percent,
    isTagEntryProduct,
    data,
    selectedSubcategoryData,
  ]);

  // Generate options list for barcode selection (matching Sales form logic)
  const defaultBarcode = productFormData.category
    ? products.find((product) => product.product_name === productFormData.category)?.rbarcode || ""
    : "";

  const barcodeOptions = [
    ...(Array.isArray(products) ? products : []).map((product) => ({
      value: product.rbarcode,
      label: product.rbarcode,
    })),
    ...(Array.isArray(data) ? data : []).map((tag) => ({
      value: tag.PCode_BarCode,
      label: tag.PCode_BarCode,
    })),
  ];


  // Ensure default barcode is included in options
  if (
    defaultBarcode &&
    !barcodeOptions.some((option) => option.value === defaultBarcode)
  ) {
    barcodeOptions.unshift({ value: defaultBarcode, label: defaultBarcode });
  }

  // Set default barcode when category changes
  useEffect(() => {
    if (!productFormData.code && defaultBarcode) {
      handleBarcodeChange(defaultBarcode);
    }
  }, [productFormData.category, defaultBarcode, handleBarcodeChange, productFormData.code]);

  // Custom handleChange function with rate calculation logic
  const handleCustomChange = (e) => {
    const { name, value } = e.target;
    
    // Call the original handleChange first
    handleChange(e);
    
    // Apply rate calculation logic for custom purity
    if (name === 'custom_purity' && productFormData.selling_purity === 'Manual') {
      const purityValue = parseFloat(value);
      let calculatedRate = '';
      
      if (purityValue === 91.6) {
        calculatedRate = rates.rate_22crt || '';
      } else if (purityValue === 99.9) {
        calculatedRate = rates.rate_24crt || '';
      } else if (purityValue === 75) {
        calculatedRate = rates.rate_18crt || '';
      }
      
      if (calculatedRate) {
        setProductFormData(prev => ({
          ...prev,
          rate: calculatedRate
        }));
      }
    }
  };

  // Clear form function
  const handleClear = () => {
    clearForm();
    setErrors({});
  };

  // Auto-calculation effects - now handled by the custom hook
  useEffect(() => {
    updateFormDataWithCalculations(productFormData, calculations, setProductFormData);
  }, [calculations, productFormData, updateFormDataWithCalculations]);

  // Total price change handler - now using the hook utility
  const handleTotalPriceChangeWrapper = () => {
    handleTotalPriceChange(productFormData, calculations, setProductFormData, setIsManualTotalPriceChange);
  };

  // Navigation handlers
  const handleNavigateToItemMaster = () => {
    if (!isTagEntryProduct) {
      navigate("/itemmaster");
    }
  };

  const handleNavigateToSubCategory = () => {
    if (!isTagEntryProduct) {
      navigate("/subcategoryv2", {
          state: { orderDetails: [productFormData], from: "/estimates/v2" },
      });
    }
  };

  // Render image section
  const renderImageSection = () => (
    <Col xs={12} md={2}>
      <DropdownButton
        id="dropdown-basic-button"
        title="Choose / Capture Image"
        variant="primary"
        size="sm"
        disabled={isTagEntryProduct}
        onClick={() => !isTagEntryProduct && setShowOptions(!showOptions)}
      >
        {showOptions && (
          <>
            <Dropdown.Item
              onClick={() => !isTagEntryProduct && fileInputRef.current && fileInputRef.current.click()}
              disabled={isTagEntryProduct}
            >
              <FaUpload /> Choose Image
            </Dropdown.Item>
            <Dropdown.Item 
              onClick={() => !isTagEntryProduct && setShowWebcam(true)}
              disabled={isTagEntryProduct}
            >
              <FaCamera /> Capture Image
            </Dropdown.Item>
          </>
        )}
      </DropdownButton>

      <input
        type="file"
        name="image"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleImageChange}
        disabled={isTagEntryProduct}
      />

      {showWebcam && (
        <div>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width={150}
            height={150}
          />
          <Button
            variant="success"
            size="sm"
            onClick={captureImage}
            disabled={isTagEntryProduct}
            style={{ marginRight: "5px" }}
          >
            Capture
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowWebcam(false)}
            disabled={isTagEntryProduct}
          >
            Cancel
          </Button>
        </div>
      )}

      {productFormData.imagePreview && (
        <div style={{ position: "relative", display: "inline-block", marginTop: "10px" }}>
          <img
            src={productFormData.imagePreview}
            alt="Selected"
            style={{ width: "100px", height: "100px", borderRadius: "8px" }}
          />
          <button
            type="button"
            onClick={clearImage}
            disabled={isTagEntryProduct}
            style={{
              position: "absolute",
              top: "5px",
              right: "5px",
              background: "transparent",
              border: "none",
              color: "red",
              fontSize: "16px",
              cursor: isTagEntryProduct ? "not-allowed" : "pointer",
              opacity: isTagEntryProduct ? 0.5 : 1,
              zIndex: 10,
            }}
          >
            <FaTrash />
          </button>
        </div>
      )}
    </Col>
  );

  // Render fixed pricing fields
  const renderFixedPricingFields = () => (
    <>
      <Col xs={12} md={2}>
        <InputField
          label="Printing Purity"
          name="printing_purity"
          value={productFormData.printing_purity || ""}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={2}>
        <InputField
          label="Piece Cost"
          name="pieace_cost"
          value={productFormData.pieace_cost}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Qty"
          name="qty"
          value={productFormData.qty}
          onChange={handleChange}
          readOnly={!isQtyEditable}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={2}>
        <InputField
          label="Taxable Amt"
          name="piece_taxable_amt"
          value={productFormData.piece_taxable_amt}
          onChange={handleChange}
          readOnly
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Tax%"
          name="tax_percent"
          value={productFormData.tax_percent}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Tax Amt"
          name="tax_amt"
          value={productFormData.tax_amt || "0.00"}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          readOnly
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="MRP"
          name="mrp_price"
          value={productFormData.mrp_price || "0.00"}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          readOnly={false}
        />
      </Col>
      <Col xs={12} md={2}>
        <InputField
          label="Total Price"
          name="total_price"
          value={productFormData.total_price || "0.00"}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          readOnly
        />
      </Col>
      {/* {renderImageSection()} */}
    </>
  );

  // Render weight-based pricing fields
  const renderWeightBasedPricingFields = () => (
    <>
      <Col xs={12} md={2}>
        {isRunningTag ? (
          <InputField
            label="Selling Purity"
            name="selling_purity"
            value={
              (() => {
                const calculatedPurity = calculateSellingPurityForRunningTag();
                const rateValue = productFormData.rate || "";
                
                if (calculatedPurity && rateValue) {
                  return `${calculatedPurity} (${rateValue})`;
                } else if (calculatedPurity) {
                  return calculatedPurity;
                }
                return productFormData.custom_purity || productFormData.selling_purity || "";
              })()
            }
            onChange={handleChange}
            readOnly
            disabled={!isToday || isTagEntryProduct}
          />
        ) : (
        <InputField
          label="Selling Purity"
          name="selling_purity"
          type="select"
          value={productFormData.selling_purity}
          onChange={handleChange}
          options={[
            ...(productFormData.product_name
              ? [
                {
                  label:
                    subcategoryOptions.find(
                      (option) => option.value === productFormData.product_name
                    )?.selling_purity || "Default Purity",
                  value:
                    subcategoryOptions.find(
                      (option) => option.value === productFormData.product_name
                    )?.selling_purity || "",
                },
              ]
              : []),
            { label: "Manual", value: "Manual" },
          ]}
          disabled={!isToday || isTagEntryProduct}
        />
        )}
      </Col>

      {productFormData.selling_purity === "Manual" && (
        <Col xs={12} md={2}>
          <InputField
            label="Custom Purity %"
            name="custom_purity"
            value={productFormData.custom_purity || ""}
            onChange={handleCustomChange}
          />
        </Col>
      )}

      <Col xs={12} md={1}>
        <InputField
          label="Gross Wt"
          name="gross_weight"
          type="number"
          value={productFormData.gross_weight || ""}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Stone Wt"
          name="stone_weight"
          type="number"
          value={productFormData.stone_weight || ""}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="St Price"
          name="stone_price"
          type="number"
          value={productFormData.stone_price || ""}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Weight BW"
          name="weight_bw"
          value={productFormData.weight_bw == 0.00 ? "" : productFormData.weight_bw}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          readOnly
        />
      </Col>
      <Col xs={12} md={2}>
        <InputField
          label="Wastage On"
          name="va_on"
          type="select"
          value={productFormData.va_on || ""}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          options={[
            { value: "Gross Weight", label: "Gross Weight" },
            { value: "Weight BW", label: "Weight BW" },
            ...(productFormData.va_on &&
              !["Gross Weight", "Weight BW"].includes(productFormData.va_on)
              ? [{ value: productFormData.va_on, label: productFormData.va_on }]
              : []),
          ]}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Wastage%"
          name="va_percent"
          type="number"
          value={productFormData.va_percent == 0 ? "" : productFormData.va_percent}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="W.Wt"
          name="wastage_weight"
          value={productFormData.wastage_weight == 0.00 ? "" : productFormData.wastage_weight}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          readOnly
        />
      </Col>
      <Col xs={12} md={2}>
        <InputField
          label="Total Weight AW"
          name="total_weight_av"
          value={productFormData.total_weight_av == 0.00 ? "" : productFormData.total_weight_av}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          readOnly
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Rate"
          name="rate"
          type="number"
          value={productFormData.rate}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Amount"
          name="rate_amt"
          value={productFormData.rate_amt == 0.00 ? "" : productFormData.rate_amt}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
          readOnly={true}
        />
      </Col>
      <Col xs={12} md={2}>
        <InputField
          label="MC On"
          name="mc_on"
          type="select"
          value={productFormData.mc_on || ""}
          onChange={handleChange}
          options={[
            { value: "MC / Gram", label: "MC / Gram" },
            { value: "MC / Piece", label: "MC / Piece" },
            { value: "MC %", label: "MC %" },
            ...(productFormData.mc_on &&
              !["MC / Gram", "MC / Piece", "MC %"].includes(productFormData.mc_on)
              ? [{ value: productFormData.mc_on, label: productFormData.mc_on }]
              : []),
          ]}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label={productFormData.mc_on === "MC %" ? "MC %" : "MC/Gm"}
          name="mc_per_gram"
          type="number"
          value={productFormData.mc_per_gram || ""}
          onChange={handleChange}
          readOnly={productFormData.mc_on === "MC / Piece"}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Total MC"
          name="making_charges"
          type="number"
          value={productFormData.making_charges == 0.00 ? "" : productFormData.making_charges}
          onChange={handleChange}
          disabled={productFormData.mc_on === "MC / Gram" || !isToday}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="HM Charge"
          name="hm_charges"
          type="number"
          value={productFormData.hm_charges || "0.00"}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Tax%"
          name="tax_percent"
          value={productFormData.tax_percent}
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={12} md={1}>
        <InputField
          label="Tax Amt"
          name="tax_amt"
          value={
            productFormData.tax_amt !== "" && productFormData.tax_amt !== null
              ? parseFloat(productFormData.tax_amt).toFixed(2)
              : ""
          }
          onChange={handleChange}
          readOnly
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={10} md={2}>
        <InputField
          label="Total Price"
          name="total_price"
          value={
            productFormData.total_price !== "" && productFormData.total_price !== null
              ? parseFloat(productFormData.total_price).toFixed(2)
              : ""
          }
          onChange={handleChange}
          disabled={!isToday || isTagEntryProduct}
        />
      </Col>
      <Col xs={2} md={1} className="d-flex align-items-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleTotalPriceChangeWrapper}
          disabled={!isManualTotalPriceChange}
          style={{
            backgroundColor: "#a36e29",
            borderColor: "#a36e29",
            padding: "4px 7px",
            marginBottom: "15px",
            marginLeft: "-1px",
            fontSize: "13px",
          }}
        >
          Change
        </button>
      </Col>
    </>
  );

  return (
    <Col>
      <div className="product-form-wrapper">
        <Row>
          {/* Barcode Field */}
          <Col xs={12} md={2}>
            <InputField
              label="BarCode/Rbarcode"
              name="code"
              value={productFormData.code || defaultBarcode}
              onChange={(e) => handleBarcodeChange(e.target.value)}
              type="select"
              options={barcodeOptions}
              disabled={!isToday}
            />
          </Col>

          {/* Category Field with Add Button */}
          <Col xs={12} md={2} className="d-flex align-items-center">
            <div style={{ flex: 1 }}>
              <InputField
                label="Category"
                name="category"
                value={productFormData.category || ""}
                type="select"
                onChange={handleChange}
                options={categoryOptions}
                disabled={!isToday || isTagEntryProduct}
              />
            </div>
            <AiOutlinePlus
              size={20}
              color="black"
              style={{
                marginLeft: "10px",
                cursor: isTagEntryProduct ? "not-allowed" : "pointer",
                marginBottom: "20px",
                opacity: isTagEntryProduct ? 0.5 : 1,
              }}
              onClick={handleNavigateToItemMaster}
            />
          </Col>

          {/* Metal Type Field */}
          <Col xs={12} md={2}>
            <InputField
              label="Metal Type"
              name="metal_type"
              value={productFormData.metal_type || ""}
              onChange={(e) => handleMetalTypeChange(e.target.value)}
              type="select"
              options={metaltypeOptions}
              disabled={!isToday || isTagEntryProduct}
            />
          </Col>

          {/* Sub Category Field with Add Button */}
          <Col xs={12} md={2} className="d-flex align-items-center">
            <div style={{ flex: 1 }}>
              <InputField
                label="Sub Category"
                name="product_name"
                value={productFormData.product_name || ""}
                onChange={(e) => handleProductNameChange(e.target.value)}
                type="select"
                options={subcategoryOptions}
                disabled={!isToday || isTagEntryProduct}
              />
            </div>
            <AiOutlinePlus
              size={20}
              color="black"
              style={{
                marginLeft: "10px",
                cursor: isTagEntryProduct ? "not-allowed" : "pointer",
                marginBottom: "20px",
                opacity: isTagEntryProduct ? 0.5 : 1,
              }}
              onClick={handleNavigateToSubCategory}
            />
          </Col>

          {/* Design Name Field */}
          <Col xs={12} md={2}>
            <InputField
              label="Product Design Name"
              name="design_name"
              value={productFormData.design_name}
              onChange={(e) => handleDesignNameChange(e.target.value)}
              type="select"
              options={designOptions}
              disabled={!isToday || isTagEntryProduct}
            />
          </Col>

          {/* Pricing Field */}
          <Col xs={12} md={2}>
            <InputField
              label="Pricing"
              name="pricing"
              type="select"
              value={productFormData.pricing || ""}
              onChange={handleChange}
              options={[
                { value: "By Weight", label: "By Weight" },
                { value: "By fixed", label: "By fixed" },
                { value: "By Running Tag", label: "By Running Tag" },
                ...(productFormData.pricing &&
                  !["By Weight", "By fixed", "By Running Tag"].includes(productFormData.pricing)
                  ? [{ value: productFormData.pricing, label: productFormData.pricing }]
                  : []),
              ]}
              disabled={!isToday || isTagEntryProduct}
            />
          </Col>

          {/* Conditional Fields Based on Pricing */}
          {isByFixed ? renderFixedPricingFields() : renderWeightBasedPricingFields()}
        </Row>

        {/* New Row for Remarks and Action Buttons */}
        <Row>
          {/* Remarks Type Field */}
          <Col xs={12} md={2}>
            <InputField
              label="Remarks Type"
              name="remarks_type"
              type="select"
              value={productFormData.remarks_type || ""}
              onChange={handleChange}
              options={[
                { value: "Client Facing", label: "Client Facing" },
                { value: "Internal Facing", label: "Internal Facing" },
              ]}
              disabled={!isToday || isTagEntryProduct}
            />
          </Col>

          {/* Remarks Field */}
          <Col xs={12} md={4}>
            <InputField
              label="Remarks"
              name="remarks"
              value={productFormData.remarks}
              onChange={handleChange}
            />
          </Col>

          {renderImageSection()}
          {/* Action Buttons */}
          <Col xs={12} md={1}>
            <Button
              onClick={isEditing ? handleUpdate : handleAdd}
              disabled={isTagEntryProduct && !isRunningTag}
              style={{
                backgroundColor: "#a36e29",
                borderColor: "#a36e29",
                padding: "4px 7px",
                marginTop: "5px",
                marginLeft: "-1px",
                fontSize: "13px",
              }}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </Col>
          <Col xs={12} md={1}>
            <Button
              variant="secondary"
              onClick={handleClear}
              disabled={isTagEntryProduct && !isRunningTag}
              style={{
                backgroundColor: "gray",
                marginLeft: "-52px",
                padding: "4px 7px",
                fontSize: "13px",
                marginTop: "5px",
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </div>
    </Col>
  );
};

export default ProductForm;
